// Sign-out's half of a rotation in flight (#1730, spec section 6.12).
//
// ── WHY SIGN-OUT IS NOT ALLOWED TO REFUSE ──────────────────────────────────
//
// The obvious fix for "signing out strands a rotation" is to block sign-out
// while one is running. It is the wrong one. The wipe sign-out performs is the
// whole point of sign-out — a member handing a shared phone back needs their
// key gone — so a rotation must never be able to keep key material on a device
// the member has finished with. Nor should a member be told they cannot leave.
//
// ── SO IT ABORTS INSTEAD, WHEN AN ABORT IS STILL POSSIBLE ──────────────────
//
// MK2 exists on exactly one device and both exits from a rotation are signed
// with it, so a sign-out that simply wipes takes the rotation with it. But the
// device is still holding MK2 at this point, and the server accepts an abort
// while no row has moved yet — which is the state a rotation spends most of its
// life in, since begin is a tap and the walk is a separate one.
//
// Once rows HAVE moved the server refuses the abort, and this gives up quietly:
// there is nothing a client can do about that today (`EncryptionModeGuard`
// refuses writes under the committed generation, so the walk cannot run
// backwards), and the member's key is recoverable through their recovery code
// or passphrase in any case (api#1146, api#1147). Saying so here rather than
// leaving the reader to wonder why the failure is swallowed.
//
// ── AND NEVER DELAYS THE SIGN-OUT ──────────────────────────────────────────
//
// Same contract as `revokeThisDeviceOnLogout`, for the same reason: this runs
// between the member tapping Sign out and their key leaving the phone. It never
// rejects and never outlives its timeout.
import { abortRotation } from '@/utils/courses-encryption/rotation';

/** How long sign-out will wait for the abort before giving up on it. */
export const ABORT_TIMEOUT_MS = 4000;

/** Resolves to `null` if `work` has not settled in time; never leaves a timer behind. */
function withTimeout(work, timeoutMs) {
  let timer = null;
  const expiry = new Promise(resolve => {
    timer = setTimeout(() => resolve(null), timeoutMs);
  });
  return Promise.race([work, expiry]).finally(() => clearTimeout(timer));
}

/**
 * Abandon a rotation this device is running, on the way out of a sign-out.
 *
 * **Never rejects, and never outlives its timeout.** Must run before
 * `courses-devices/reset` and before `courses-keys/wipe`: the abort is signed
 * with the pending key, which is read from the status snapshot that mutation
 * drops and unwrapped with the MK2 that wipe destroys.
 *
 * @param {import('vuex').Store} store
 * @param {{ timeoutMs?: number, deps?: object }} [options]
 * @returns {Promise<boolean>} whether a rotation was actually abandoned
 */
export async function abortRotationOnLogout(store, { timeoutMs = ABORT_TIMEOUT_MS, deps } = {}) {
  const pendingKeyId = store?.getters?.['courses-devices/pendingKeyId'];
  if (!pendingKeyId) return false;

  // Only the device holding MK2 can sign the abort. Any other device signing
  // out is not this rotation's business, and the API would refuse it anyway —
  // asking would spend a challenge from the member's rate limit to be told so.
  const held = store.getters['courses-keys/pendingKeyMaterial'];
  if (!held || held.keyId !== pendingKeyId) return false;

  const done = await withTimeout(
    abortRotation({ store, deps })
      .then(() => true)
      .catch(error => {
        // The expected refusal is ROWS_REMAIN — rows have already moved, and no
        // client can put them back. Logged, never surfaced: the member is
        // signing out and there is nothing for them to act on.
        console.warn('[CoursesE2EE] The rotation was not abandoned on sign-out:', error?.code);
        return false;
      }),
    timeoutMs,
  );
  return done === true;
}
